import { useEffect, useRef, useState } from "react";

/**
 * useReveal
 * Fires once when the element enters the viewport.
 * @param {IntersectionObserverInit} options
 * @returns {{ ref: React.RefObject, inView: boolean }}
 */
export function useReveal(options = {}) {
  const ref    = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -50px 0px", ...options }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

/**
 * useCounter
 * Animates an integer from 0 → target (ms duration) once `run` becomes true.
 * @param {number} target
 * @param {boolean} run
 * @param {number} duration  milliseconds
 * @returns {number}
 */
export function useCounter(target, run, duration = 1200) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!run) return;
    let startTime = null;

    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [run, target, duration]);

  return value;
}